import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Plus, Copy, Check } from "lucide-react";
import { RecruiterShell } from "@/shell/RecruiterShell";
import { api } from "@/lib/api";
import { copyToClipboard } from "@/lib/format";
import type { CandidateDetailData, CandidateInviteDetail } from "@/lib/types";

// ── Status badge ──────────────────────────────────────────────────────────────

type InviteDetailStatus = "invited" | "in_progress" | "completed" | "expired" | string;

interface BadgeCfg {
  label: string;
  bg: string;
  color: string;
  border: string;
}

function statusConfig(status: InviteDetailStatus): BadgeCfg {
  switch (status) {
    case "invited":
      return {
        label: "NOT STARTED",
        bg: "transparent",
        color: "#7C7590",
        border: "1px solid #9A93AD",
      };
    case "in_progress":
      return {
        label: "IN PROGRESS",
        bg: "#F5ECD6",
        color: "#B5811F",
        border: "1px solid #E5C97A",
      };
    case "completed":
      return {
        label: "COMPLETED",
        bg: "#E6F5EE",
        color: "#22A06B",
        border: "1px solid #A8D4BC",
      };
    case "expired":
      return {
        label: "EXPIRED",
        bg: "#FFFFFF",
        color: "#8C86A0",
        border: "1px solid #D8D2C8",
      };
    default:
      return {
        label: status.toUpperCase().replace(/_/g, " "),
        bg: "#FFFFFF",
        color: "#7C7590",
        border: "1px solid #D8D2C8",
      };
  }
}

function StatusBadge({ status }: { status: InviteDetailStatus }) {
  const cfg = statusConfig(status);
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "3px 10px",
        borderRadius: 999,
        background: cfg.bg,
        color: cfg.color,
        border: cfg.border,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.06em",
        fontFamily: "var(--font-mono, ui-monospace, monospace)",
        whiteSpace: "nowrap",
      }}
    >
      {cfg.label}
    </span>
  );
}

// ── Copy button ───────────────────────────────────────────────────────────────

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    copyToClipboard(text).then((ok) => {
      if (ok) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    });
  }

  return (
    <button
      onClick={handleCopy}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "6px 14px",
        height: 34,
        border: "1px solid #EFEBF7",
        borderRadius: 8,
        background: copied ? "#E6F5EE" : "#FBFAFE",
        color: copied ? "#22A06B" : "#17131F",
        fontSize: 12.5,
        fontWeight: 600,
        cursor: "pointer",
        whiteSpace: "nowrap",
        flexShrink: 0,
        transition: "background 0.15s, color 0.15s",
      }}
      aria-label="Copy invite link"
    >
      {copied ? (
        <>
          <Check size={13} />
          Copied!
        </>
      ) : (
        <>
          <Copy size={13} />
          Copy
        </>
      )}
    </button>
  );
}

// ── Invite card ───────────────────────────────────────────────────────────────

interface InviteCardProps {
  invite: CandidateInviteDetail;
  onArchive: (id: number) => void;
  onErase: (id: number) => void;
  candidateEmail: string;
}

function InviteCard({ invite, onArchive, onErase, candidateEmail }: InviteCardProps) {
  const [archiving, setArchiving] = useState(false);
  const [erasing, setErasing] = useState(false);
  const [brief, setBrief] = useState(invite.company_brief_html);
  const [researching, setResearching] = useState(false);
  const [researchError, setResearchError] = useState<string | null>(null);

  async function handleResearch() {
    if (researching) return;
    setResearching(true);
    setResearchError(null);
    try {
      const res = await api.post<{ ok: boolean; brief_html?: string; error?: string }>(
        `/recruiter/invite/${invite.id}/research`,
        {},
      );
      if (!res.ok) {
        setResearchError(res.error ?? "Could not research company.");
      } else {
        setBrief(res.brief_html ?? "");
      }
    } catch (err) {
      setResearchError(err instanceof Error ? err.message : "Could not research company.");
    } finally {
      setResearching(false);
    }
  }

  async function handleArchive() {
    if (archiving) return;
    setArchiving(true);
    try {
      await api.post(`/recruiter/invite/${invite.id}/archive`, {});
      onArchive(invite.id);
    } catch {
      setArchiving(false);
    }
  }

  async function handleErase() {
    if (erasing) return;
    if (!window.confirm(`Permanently erase all data for ${candidateEmail}? This anonymises their email and deletes their session files (GDPR). This cannot be undone.`)) return;
    setErasing(true);
    try {
      await api.post(`/recruiter/candidate/${invite.id}/erase`, {});
      onErase(invite.id);
    } catch (err) {
      window.alert(`Error: ${err instanceof Error ? err.message : "unknown"}`);
      setErasing(false);
    }
  }

  // Format the date simply
  const dateLabel = (() => {
    const raw = invite.created_at;
    if (!raw) return "";
    try {
      return new Date(raw).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } catch {
      return raw;
    }
  })();

  return (
    <div
      style={{
        background: "#FFFFFF",
        border: "1px solid #EFEBF7",
        borderRadius: 18,
        padding: "20px 22px",
        boxShadow: "0 1px 3px rgba(45,30,90,.06)",
      }}
    >
      {/* Card header: title + badge */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 6,
        }}
      >
        <div
          style={{
            fontSize: 15,
            fontWeight: 700,
            color: "#17131F",
            lineHeight: 1.3,
          }}
        >
          {invite.role}
          {invite.company ? ` at ${invite.company}` : ""}
        </div>
        <StatusBadge status={invite.status} />
      </div>

      {/* Subtitle: persona · mode · date */}
      <div
        style={{
          fontSize: 12.5,
          color: "#7C7590",
          marginBottom: 16,
          lineHeight: 1.5,
        }}
      >
        {[invite.persona_label, invite.mode_label, dateLabel]
          .filter(Boolean)
          .join(" · ")}
      </div>

      {/* Invite link row */}
      <div style={{ marginBottom: 16 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: "#8C86A0",
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            fontFamily: "var(--font-mono, ui-monospace, monospace)",
            marginBottom: 6,
          }}
        >
          Invite link
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input
            type="text"
            readOnly
            value={invite.invite_url}
            style={{
              flex: 1,
              minWidth: 0,
              padding: "7px 11px",
              fontSize: 12.5,
              color: "#7C7590",
              background: "#F3F1FA",
              border: "1px solid #EFEBF7",
              borderRadius: 8,
              fontFamily: "var(--font-mono, ui-monospace, monospace)",
              outline: "none",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            onFocus={(e) => e.currentTarget.select()}
          />
          <CopyButton text={invite.invite_url} />
        </div>
      </div>

      {/* Scorecard link — resolves to the finished attempt, not the latest one */}
      {invite.scorecard_session_id && (
        <div style={{ marginBottom: 14 }}>
          <a
            href={`/coach/recruiter/scorecard/${invite.scorecard_session_id}`}
            style={{
              fontSize: 13.5,
              fontWeight: 600,
              color: "var(--accent)",
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            View scorecard →
          </a>
        </div>
      )}

      {/* Every practice session, newest first.
          A candidate can retake an interview as often as they like, and each
          attempt is its own session with its own scorecard. Before this the
          screens only ever showed the most recent one, so a finished interview
          disappeared the moment the candidate pressed Practise again. */}
      {invite.attempts && invite.attempts.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <p
            style={{
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: 10,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.14em",
              color: "#7C7590",
              marginBottom: 8,
            }}
          >
            Practice sessions ({invite.attempt_count})
            {invite.completed_count > 0 && ` · ${invite.completed_count} completed`}
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {invite.attempts.map((a, i) => (
              <div
                key={a.session_id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "8px 10px",
                  borderRadius: 10,
                  border: "1px solid #EFEBF7",
                  background: a.completed ? "transparent" : "#FBFAFE",
                }}
              >
                <span
                  style={{
                    fontFamily: "'JetBrains Mono',monospace",
                    fontSize: 11,
                    color: "#7C7590",
                    minWidth: 26,
                  }}
                >
                  #{invite.attempts.length - i}
                </span>
                <span style={{ fontSize: 13, color: "#17131F", flex: 1 }}>
                  {a.started_at ? new Date(a.started_at).toLocaleDateString("en-GB", {
                    day: "numeric", month: "short", year: "numeric",
                  }) : "Date unknown"}
                </span>
                {a.completed ? (
                  <>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#17131F" }}>
                      {a.score ?? "—"}
                    </span>
                    <a
                      href={`/coach/recruiter/scorecard/${a.session_id}`}
                      style={{
                        fontSize: 12.5,
                        fontWeight: 600,
                        color: "var(--accent)",
                        textDecoration: "none",
                      }}
                    >
                      Scorecard →
                    </a>
                  </>
                ) : (
                  <span style={{ fontSize: 12.5, color: "#7C7590" }}>
                    {a.status === "ready" || a.status === "preparing"
                      ? "Not started"
                      : "In progress"}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Company research */}
      <div style={{ marginBottom: 14 }}>
        {brief ? (
          <details>
            <summary
              style={{
                fontSize: 13.5,
                fontWeight: 600,
                color: "var(--accent)",
                cursor: "pointer",
              }}
            >
              Company research
            </summary>
            {/* Server-rendered, sanitised markdown, so "###" shows as a heading. */}
            <div
              className="prose-scorecard"
              style={{ margin: "8px 0 0", fontSize: 12.5 }}
              dangerouslySetInnerHTML={{ __html: brief }}
            />
          </details>
        ) : (
          <button
            onClick={handleResearch}
            disabled={researching}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              fontSize: 13.5,
              fontWeight: 600,
              color: researching ? "#9A93AD" : "var(--accent)",
              cursor: researching ? "default" : "pointer",
              fontFamily: "inherit",
            }}
          >
            {researching ? "Researching…" : "Research company →"}
          </button>
        )}
        {researchError && (
          <p style={{ margin: "6px 0 0", fontSize: 12.5, color: "#E1573F" }}>{researchError}</p>
        )}
      </div>

      {/* Footer actions */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          paddingTop: 12,
          borderTop: "1px solid #EFEBF7",
          flexWrap: "wrap",
        }}
      >
        <button
          onClick={handleArchive}
          disabled={archiving}
          style={{
            background: "none",
            border: "none",
            padding: 0,
            fontSize: 12.5,
            color: archiving ? "#9A93AD" : "#8C86A0",
            cursor: archiving ? "default" : "pointer",
            fontFamily: "inherit",
            textDecoration: "underline",
            textUnderlineOffset: 2,
          }}
        >
          {archiving ? "Archiving…" : "Archive this invite"}
        </button>
        <button
          onClick={handleErase}
          disabled={erasing}
          style={{
            background: "none",
            border: "none",
            padding: 0,
            fontSize: 12.5,
            color: erasing ? "#9A93AD" : "var(--accent)",
            cursor: erasing ? "default" : "pointer",
            fontFamily: "inherit",
            textDecoration: "underline",
            textUnderlineOffset: 2,
          }}
        >
          {erasing ? "Erasing…" : "Erase candidate data (GDPR)"}
        </button>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function RecruiterCandidateDetail() {
  const { email: encodedEmail = "" } = useParams<{ email: string }>();
  const email = decodeURIComponent(encodedEmail);

  const [data, setData] = useState<CandidateDetailData | null>(null);
  const [invites, setInvites] = useState<CandidateInviteDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!email) return;
    setLoading(true);
    setError(null);
    api
      .get<CandidateDetailData>(
        "/recruiter/candidate/" + encodeURIComponent(email),
      )
      .then((d) => {
        setData(d);
        setInvites(d.invites ?? []);
        setLoading(false);
      })
      .catch((err) => {
        setError(err?.message ?? "Failed to load candidate.");
        setLoading(false);
      });
  }, [email]);

  function handleArchive(id: number) {
    setInvites((prev) => prev.filter((inv) => inv.id !== id));
  }

  function handleErase(id: number) {
    setInvites((prev) => prev.filter((inv) => inv.id !== id));
  }

  const candidateName = data?.candidate_name ?? email;
  const candidateEmail = data?.candidate_email ?? email;

  return (
    <RecruiterShell activeNav="candidates">
      {/* ── Loading ───────────────────────────────────────────── */}
      {loading && (
        <div
          style={{
            padding: "64px 24px",
            textAlign: "center",
            color: "#8C86A0",
            fontSize: 14,
          }}
        >
          Loading candidate…
        </div>
      )}

      {/* ── Error ─────────────────────────────────────────────── */}
      {!loading && error && (
        <div
          style={{
            background: "#FFFFFF",
            border: "1px solid #EFEBF7",
            borderRadius: 18,
            padding: "32px 24px",
            textAlign: "center",
            color: "#E1573F",
            fontSize: 14,
          }}
        >
          {error}
        </div>
      )}

      {/* ── Content ───────────────────────────────────────────── */}
      {!loading && !error && data && (
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          {/* ── Header ──────────────────────────────────────────── */}
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 16,
              marginBottom: 28,
              flexWrap: "wrap",
            }}
          >
            <div>
              {/* Breadcrumb label */}
              <div
                style={{
                  fontFamily: "var(--font-mono, ui-monospace, monospace)",
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.14em",
                  color: "var(--accent)",
                  marginBottom: 6,
                }}
              >
                CANDIDATE
              </div>

              {/* Name */}
              <h1
                style={{
                  fontSize: 24,
                  fontWeight: 700,
                  color: "#17131F",
                  margin: "0 0 4px",
                  lineHeight: 1.2,
                }}
              >
                {candidateName}
              </h1>

              {/* Email */}
              <a
                href={`mailto:${candidateEmail}`}
                style={{
                  fontSize: 14,
                  color: "#7C7590",
                  textDecoration: "none",
                }}
              >
                {candidateEmail}
              </a>
            </div>

            {/* New invite button */}
            <a
              href={`/coach/recruiter/invite/new?email=${encodeURIComponent(candidateEmail)}`}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "0 20px",
                height: 40,
                borderRadius: 999,
                background: "var(--accent)",
                color: "#fff",
                fontSize: 13.5,
                fontWeight: 600,
                textDecoration: "none",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              <Plus size={15} strokeWidth={2.5} />
              New invite
            </a>
          </div>

          {/* ── Invite cards ──────────────────────────────────────── */}
          {invites.length === 0 ? (
            <div
              style={{
                background: "#FFFFFF",
                border: "1px solid #EFEBF7",
                borderRadius: 18,
                padding: "48px 24px",
                textAlign: "center",
                color: "#7C7590",
                fontSize: 14,
              }}
            >
              No invites yet. Send the first invite to get started.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {invites.map((invite) => (
                <InviteCard
                  key={invite.id}
                  invite={invite}
                  onArchive={handleArchive}
                  onErase={handleErase}
                  candidateEmail={candidateEmail}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Mobile viewport override ─────────────────────────── */}
      <style>{`
        @media (max-width: 720px) {
          .rcd-header { flex-direction: column; }
        }
      `}</style>
    </RecruiterShell>
  );
}
